import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import { ExpandMore, ExpandLess, HelpOutline } from '@mui/icons-material';
import React, { useState } from 'react';
import { InfoDialog } from '../InfoDialog';

interface AdvancedSectionProps {
  children: React.ReactNode;
  /** Whether advanced settings are currently configured (shows indicator when collapsed) */
  hasSettings?: boolean;
  /** Title for the help dialog */
  helpTitle?: string;
  /** Subtitle for the help dialog */
  helpSubtitle?: string;
  /** Content for the help dialog */
  helpContent?: React.ReactNode;
  /** Max width of the help dialog */
  helpMaxWidth?: Breakpoint;
  /** Initially expanded state */
  defaultExpanded?: boolean;
}

export const AdvancedSection = ({
  children,
  hasSettings = false,
  helpTitle = 'Advanced Options',
  helpSubtitle,
  helpContent,
  helpMaxWidth = 'sm',
  defaultExpanded = false,
}: AdvancedSectionProps): React.JSX.Element => {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [showHelp, setShowHelp] = useState<boolean>(false);

  return (
    <>
      <InfoDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title={helpTitle}
        subtitle={helpSubtitle}
        maxWidth={helpMaxWidth}
      >
        {helpContent}
      </InfoDialog>
      <Stack direction={'column'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {helpContent && (
            <IconButton size="small" onClick={() => setShowHelp(true)} sx={{ visibility: 'hidden' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          )}
          <Divider sx={{ flex: 1 }} />
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="caption" color="text.secondary" sx={{ userSelect: 'none' }}>
              Advanced
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ opacity: hasSettings && !expanded ? 1 : 0, transition: 'opacity 0.15s ease' }}
            >
              *
            </Typography>
            {expanded ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
          </Stack>
          <Divider sx={{ flex: 1 }} />
          {helpContent && (
            <IconButton
              size="small"
              onClick={() => setShowHelp(true)}
              sx={{ visibility: expanded ? 'visible' : 'hidden', color: 'text.secondary' }}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Collapse in={expanded}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {children}
          </Stack>
        </Collapse>
      </Stack>
    </>
  );
};
